import { useStore } from '@/store/useStore';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { Settings } from 'lucide-react';
import { adminApi } from '@/utils/api';
import { useNotify } from '@/context/NotificationContext';
import { useState } from 'react';

export const ControllerManager = () => {
    const { animationsEnabled, setAnimationsEnabled } = useStore();
    const { impactOccurred } = useHapticFeedback();
    const { showNotification } = useNotify();
    const [isSaving, setIsSaving] = useState(false);

    const handleToggle = async () => {
        impactOccurred('medium');
        const newValue = !animationsEnabled;
        setAnimationsEnabled(newValue);
        setIsSaving(true);
        
        try {
            const res = await adminApi.setAnimationConfig(newValue);
            const data = await res.json();
            if (data.success) {
                showNotification('Animations setting updated', 'success');
            } else {
                showNotification('Failed to update server setting', 'error');
                setAnimationsEnabled(!newValue); // Revert on failure
            }
        } catch (e) {
            console.error(e);
            showNotification('Error connecting to server', 'error');
            setAnimationsEnabled(!newValue); // Revert on failure
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-[#1c1c1e] rounded-2xl p-6 border border-white/5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-bl-full blur-3xl -z-10" />
                
                <h2 className="text-xl font-black text-white mb-2 flex items-center gap-2">
                    <Settings className="text-blue-500" />
                    Controller
                </h2>
                <p className="text-sm text-white/50 mb-6">
                    Master configuration for critical application behaviors and global frontend states.
                </p>

                <div className="space-y-4">
                    {/* Animations Controller iOS Style Toggle */}
                    <div className="bg-black/20 p-4 rounded-xl border border-white/5 flex items-center justify-between">
                        <div>
                            <label className="text-xs font-bold text-white/60 uppercase tracking-wider block">
                                Edge Light Animations
                            </label>
                            <p className="text-[10px] text-white/40 mt-1 max-w-[250px]">
                                Toggle the background RGB edge lights on main pages (Home, Search, All Items). High performance impact.
                            </p>
                        </div>
                        <button
                            onClick={handleToggle}
                            disabled={isSaving}
                            className={`w-14 h-8 rounded-full p-1 transition-colors duration-300 ease-in-out ${animationsEnabled ? 'bg-green-500' : 'bg-[#39393d]'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <div
                                className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform duration-300 ease-in-out flex items-center justify-center ${animationsEnabled ? 'translate-x-6' : 'translate-x-0'}`}
                            >
                                {isSaving && <div className="w-3 h-3 border-2 border-black/20 border-t-black rounded-full animate-spin" />}
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
